import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  Briefcase,
  Laptop,
  Building2,
  Droplets,
  Hammer,
  HardHat,
  Snowflake,
  Sparkles,
  Store,
  Trees,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Field,
  Input,
} from '@/components/ui';
import { ApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { currentLocale } from '@/lib/i18n';
import { TRADE_KEYS, type TradeKey } from '@/lib/schemas';
import { TRADES } from '@/lib/trade';
import { FeaturePicker } from '@/components/feature-picker';
import { DEFAULT_FEATURES, type Feature } from '@/lib/features';
import { cn } from '@/lib/utils';
import { setupApi } from './setup-api';

// הודעות השגיאה הן מפתחות תרגום — מתורגמות ברינדור, כך שהן מתחלפות עם השפה.
const ERROR_KEYS = ['businessNameShort', 'nameRequired', 'emailRequired', 'emailInvalid', 'passwordRule'] as const;
type ErrorKey = (typeof ERROR_KEYS)[number];
const msg = (k: ErrorKey) => k;
const isErrorKey = (m: string): m is ErrorKey => (ERROR_KEYS as readonly string[]).includes(m);

// אותו כלל כמו בשרת (auth.dto PASSWORD_RULE) — כדי שהשגיאה תופיע לפני השליחה.
const schema = z.object({
  businessName: z.string().trim().min(2, msg('businessNameShort')).max(120),
  trade: z.enum(TRADE_KEYS),
  features: z.array(z.string()),
  ownerName: z.string().trim().min(1, msg('nameRequired')).max(120),
  email: z.string().trim().min(1, msg('emailRequired')).email(msg('emailInvalid')),
  password: z.string().regex(/^(?=.*[A-Za-z])(?=.*\d).{12,128}$/, msg('passwordRule')),
});
type Values = z.infer<typeof schema>;

const ICONS: Record<TradeKey, LucideIcon> = {
  HVAC: Snowflake,
  ELECTRICAL: Zap,
  PLUMBING: Droplets,
  CARPENTRY: Hammer,
  RENOVATION: HardHat,
  BUILDING_MAINTENANCE: Building2,
  CLEANING: Sparkles,
  GARDENING: Trees,
  RETAIL: Store,
  OTHER: Briefcase,
  OFFICE: Laptop,
};

export function SignupStep({ onBack }: { onBack?: () => void }) {
  const { t } = useTranslation(['onboarding', 'common']);
  // הודעה מ-zod: מפתח שלנו → תרגום; כל דבר אחר (ברירות המחדל של zod) כמו שהוא.
  const errorText = (m: string | undefined) =>
    m === undefined ? undefined : isErrorKey(m) ? t(`signup.errors.${m}`) : m;
  const { adoptSession } = useAuth();
  const qc = useQueryClient();
  const [serverError, setServerError] = useState<string | null>(null);
  const {
    register,
    control,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: {
      businessName: '',
      trade: 'HVAC',
      features: DEFAULT_FEATURES.HVAC,
      ownerName: '',
      email: '',
      password: '',
    },
  });

  const onSubmit = handleSubmit(async (values) => {
    setServerError(null);
    try {
      adoptSession(await setupApi.signup({ ...values, locale: currentLocale() }));
      await qc.invalidateQueries({ queryKey: ['onboarding-import'] });
    } catch (err) {
      setServerError(
        err instanceof ApiError && err.status === 429
          ? t('signup.tooMany')
          : err instanceof ApiError
            ? err.message
            : t('signup.failed'),
      );
    }
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t('signup.title')}</CardTitle>
        <CardDescription>
          {t('signup.description')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={(e) => void onSubmit(e)} noValidate className="grid gap-4 sm:grid-cols-2">
          {serverError ? (
            <div
              role="alert"
              className="flex items-start gap-2 rounded-(--radius-md) border border-danger-border bg-danger-subtle p-3 text-xs text-danger sm:col-span-2"
            >
              <AlertCircle className="mt-px size-4 shrink-0" aria-hidden />
              <span>{serverError}</span>
            </div>
          ) : null}

          <Field
            label={t('signup.businessName')}
            htmlFor="businessName"
            error={errorText(errors.businessName?.message)}
            required
            className="sm:col-span-2"
          >
            <Input
              autoComplete="organization"
              invalid={!!errors.businessName}
              {...register('businessName')}
            />
          </Field>

          {/* התחום קובע את המונחים, סוגי העבודות והתפריטים — אפשר לשנות בהגדרות. */}
          <fieldset className="sm:col-span-2">
            <legend className="mb-2 text-xs font-medium text-fg-muted">{t('signup.trade')}</legend>
            <Controller
              control={control}
              name="trade"
              render={({ field }) => (
                <div role="radiogroup" aria-label={t('signup.trade')} className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  {TRADE_KEYS.map((key) => {
                    const Icon = ICONS[key];
                    const on = field.value === key;
                    return (
                      <button
                        key={key}
                        type="button"
                        role="radio"
                        aria-checked={on}
                        onClick={() => {
                          field.onChange(key);
                          // תחום אחר → מה שמתאים לו. מה שהמשתמש שינה ידנית לתחום הקודם לא נגרר.
                          setValue('features', DEFAULT_FEATURES[key]);
                        }}
                        className={cn(
                          'flex min-h-20 flex-col items-center justify-center gap-1 rounded-(--radius-md) border px-2 py-2.5 text-center transition-colors',
                          on
                            ? 'border-accent bg-accent-subtle text-accent'
                            : 'border-border bg-surface text-fg hover:bg-surface-hover',
                        )}
                      >
                        <Icon className="size-5" aria-hidden />
                        <span className="text-xs font-medium">{TRADES[key].label}</span>
                        <span className="text-2xs text-fg-subtle">{TRADES[key].hint}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            />
          </fieldset>

          <fieldset className="sm:col-span-2">
            <legend className="mb-1 text-xs font-medium text-fg-muted">{t('signup.featuresTitle')}</legend>
            <p className="mb-2 text-2xs text-fg-subtle">{t('signup.featuresHint')}</p>
            <Controller
              control={control}
              name="features"
              render={({ field }) => <FeaturePicker value={field.value as Feature[]} onChange={field.onChange} />}
            />
          </fieldset>

          <Field label={t('signup.ownerName')} htmlFor="ownerName" error={errorText(errors.ownerName?.message)} required>
            <Input autoComplete="name" invalid={!!errors.ownerName} {...register('ownerName')} />
          </Field>

          <Field
            label={t('signup.email')}
            htmlFor="email"
            error={errorText(errors.email?.message)}
            hint={t('signup.emailHint')}
            required
          >
            <Input
              type="email"
              autoComplete="email"
              invalid={!!errors.email}
              {...register('email')}
            />
          </Field>

          <Field
            label={t('signup.password')}
            htmlFor="password"
            error={errorText(errors.password?.message)}
            hint={t('signup.passwordHint')}
            required
            className="sm:col-span-2"
          >
            <Input
              type="password"
              autoComplete="new-password"
              invalid={!!errors.password}
              {...register('password')}
            />
          </Field>

          <div className="flex gap-2 sm:col-span-2">
            {onBack ? (
              <Button type="button" variant="ghost" size="lg" onClick={onBack}>
                {t('common:actions.back')}
              </Button>
            ) : null}
            <Button type="submit" size="lg" loading={isSubmitting} className="flex-1">
              {t('common:actions.continue')}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
